"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import type { UserId } from "@nimble/datamodel/types/userId";
import { buildErrorMessage } from "@nimble/react-requests/helpers/error-message";
import { ActionTypeEnum } from "@nimble/recommendation-client/types";
import * as React from "react";
import { useForm, UseFormReturn, useWatch } from "react-hook-form";

import {
  rejectMatchFormSchema,
  RejectMatchFormValues,
  rejectMatchOptions,
} from "@/app/(discover)/candidate/invites/components/reject-match-modal/reject-match-form-schema";
import { RejectMatchFormView } from "@/app/(discover)/candidate/invites/components/reject-match-modal/reject-match-view";
import useInvitesInvalidQueries from "@/app/(discover)/candidate/invites/hooks/use-invites-invalid-queries";
import { withAuthGate } from "@/components/shared/auth-paywall/with-auth-gate";
import { createAction } from "@/lib/api-clients/recommendation-api-client";
import { neverthrowize } from "@/lib/data/call-server-api";
import { User } from "@/lib/types/auth";

export async function submitForm({
  data,
  form,
  userId,
  schoolNcesId,
  onSubmitSuccess,
  submitRequest = createAction,
}: {
  data: RejectMatchFormValues;
  form: UseFormReturn<RejectMatchFormValues>;
  userId: UserId;
  schoolNcesId: string;
  onSubmitSuccess: () => void;
  submitRequest?: typeof createAction;
}) {
  const result = neverthrowize([
    submitRequest(userId, {
      school_nces_id: schoolNcesId,
      action: ActionTypeEnum.CANDIDATE_REJECT,
      reason: data.reason,
      explanation: data.explanation,
      message_data: null,
      notification_data: null,
    }),
  ]);

  await result.match(onSubmitSuccess, (error: Error) => {
    form.setError("root", {
      message: buildErrorMessage(error),
    });
  });
}

export function RejectMatchFormContainerInner({
  onCancel,
  onSubmitSuccess,
  schoolNcesId,
  user,
}: {
  onCancel: () => void;
  onSubmitSuccess: () => void;
  schoolNcesId: string;
  user: User;
}) {
  const { invalidate } = useInvitesInvalidQueries(user);
  const form = useForm<RejectMatchFormValues>({
    resolver: zodResolver(rejectMatchFormSchema),
    defaultValues: {
      reason: rejectMatchOptions.NO_LONGER_LOOKING.value,
      explanation: undefined,
    },
  });

  const selectedReason = useWatch({ control: form.control, name: "reason" });

  const handleSubmitSuccess = () => {
    invalidate();
    onSubmitSuccess();
  };

  return (
    <RejectMatchFormView
      form={form}
      onCancel={onCancel}
      selectedReason={selectedReason}
      onSubmit={form.handleSubmit((data) =>
        submitForm({
          data,
          schoolNcesId,
          userId: user.id,
          form,
          onSubmitSuccess: handleSubmitSuccess,
        }),
      )}
    />
  );
}

export const RejectMatchFormContainer = withAuthGate(
  RejectMatchFormContainerInner,
  {},
);
